import React, { memo, useCallback, useEffect } from 'react';
import { batch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { NoAvailableMessageContent } from 'app/shared/components/common/NoAvailableMessage';
import _ from 'lodash';

import { IconSize } from 'constants/coreDesignSystem';
import usePrevious from 'hooks/usePrevious';
import { getConditionalObject } from 'utils/data';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { ADD_MORTGAGE_HREF } from '../../../EditModal.constants';
import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';
import { ContentHeadline } from '../sharedComponents';

import { PERCENTAGE } from './AdditionalFinancing.constants';
import AdditionalMortgageForm from './AdditionalMortgageForm';
import { getAdditionalMortgageKey } from './AdditionalMortgageForm/AdditionalMortgageForm.config';

const AdditionalFinancing = ({
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  className,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  applicationData,
  entitySideData,
  setEntitySideData,
  setConfig,
  setForm,
  setValidationErrors,
}) => {
  const history = useHistory();
  const { section, applicationId, tab, subtab, entityId } = useParams();
  const { pathname } = useParams();

  const {
    additionalMortgages: savedAdditionalMortgages,
    initiallyNewAddedAdditionalMortgagesFieldsKeys,
  } = applicationData;
  const { newAddedAdditionalMortgages = [] } = entitySideData.additionalMortgages ?? {};
  const additionalMortgagesKeys = Object.keys(
    form.additionalMortgages ?? savedAdditionalMortgages ?? {},
  );
  const prevIsLoading = usePrevious(isLoading);

  const onDeleteAdditionalMortgage = useCallback(
    fieldKeyToRemove => {
      batch(() => {
        const isAdditionalMortgageSaved = !!savedAdditionalMortgages[fieldKeyToRemove];
        const isInitiallyNewAdded =
          initiallyNewAddedAdditionalMortgagesFieldsKeys.includes(fieldKeyToRemove);

        setForm(prev => {
          const { [fieldKeyToRemove]: mortgageToRemove, ...restMortgages } =
            prev.additionalMortgages;

          return {
            ...prev,
            additionalMortgages: {
              ...restMortgages,
              ...getConditionalObject(isAdditionalMortgageSaved && !isInitiallyNewAdded, {
                [fieldKeyToRemove]: {},
              }),
            },
          };
        });

        setEntitySideData(prev => {
          const { additionalMortgages = {}, ...restEntitySideData } = prev;
          const { deletedAdditionalMortgages, newAddedAdditionalMortgages } = additionalMortgages;

          const updatedDeletedAdditionalMortgages = isAdditionalMortgageSaved
            ? [...(deletedAdditionalMortgages ?? []), fieldKeyToRemove]
            : deletedAdditionalMortgages;

          const updatedNewAddedAdditionalMortgages = _.without(
            newAddedAdditionalMortgages,
            fieldKeyToRemove,
          );

          return {
            ...restEntitySideData,
            ...getConditionalObject(
              updatedNewAddedAdditionalMortgages?.length ||
                updatedDeletedAdditionalMortgages?.length,
              () => ({
                additionalMortgages: {
                  ...getConditionalObject(updatedNewAddedAdditionalMortgages?.length, {
                    newAddedAdditionalMortgages: updatedNewAddedAdditionalMortgages,
                  }),
                  ...getConditionalObject(updatedDeletedAdditionalMortgages?.length, {
                    deletedAdditionalMortgages: updatedDeletedAdditionalMortgages,
                  }),
                },
              }),
            ),
          };
        });

        if (!isAdditionalMortgageSaved || isInitiallyNewAdded) {
          setConfig(prev => {
            return {
              ...prev,
              groups: prev?.groups.filter(({ key }) => key !== `${fieldKeyToRemove}-form`),
            };
          });
        }
      });
    },

    [
      setConfig,
      setForm,
      setEntitySideData,
      savedAdditionalMortgages,
      initiallyNewAddedAdditionalMortgagesFieldsKeys,
    ],
  );

  const onRestoreAdditionalMortgage = useCallback(
    fieldKey => {
      setEntitySideData(prev => {
        const { additionalMortgages = {}, ...restEntitySideData } = prev;
        const { deletedAdditionalMortgages = [], newAddedAdditionalMortgages = [] } =
          additionalMortgages;

        const updatedDeletedAdditionalMortgages = _.without(deletedAdditionalMortgages, fieldKey);

        return {
          ...restEntitySideData,
          ...getConditionalObject(
            newAddedAdditionalMortgages.length || updatedDeletedAdditionalMortgages?.length,
            () => ({
              additionalMortgages: {
                ...getConditionalObject(newAddedAdditionalMortgages.length, {
                  newAddedAdditionalMortgages,
                }),
                ...getConditionalObject(updatedDeletedAdditionalMortgages?.length, {
                  deletedAdditionalMortgages: updatedDeletedAdditionalMortgages,
                }),
              },
            }),
          ),
        };
      });
    },
    [setEntitySideData],
  );

  const onAddAdditionalMortgage = useCallback(() => {
    const fieldKey = getAdditionalMortgageKey(_.uniqueId('new-'));

    setForm(prev => {
      return {
        ...prev,
        additionalMortgages: {
          ...prev.additionalMortgages,
          [fieldKey]: { isNewAdded: true },
        },
      };
    });

    setEntitySideData(prev => {
      const { newAddedAdditionalMortgages = [], ...rest } = prev.additionalMortgages ?? {};

      return {
        ...prev,
        additionalMortgages: {
          ...rest,
          newAddedAdditionalMortgages: [fieldKey, ...newAddedAdditionalMortgages],
        },
      };
    });
  }, [setForm, setEntitySideData]);

  useEffect(() => {
    if (prevIsLoading === undefined || isLoading || section !== ADD_MORTGAGE_HREF) {
      return;
    }

    onAddAdditionalMortgage();

    const path = LoansPaths.applicationManagement.generatePath({
      applicationId,
      tab,
      subtab,
      entityId,
      pathFrom: pathname,
    });

    history.push(path);
  }, [
    section,
    applicationId,
    tab,
    subtab,
    entityId,
    pathname,
    history,
    isLoading,
    prevIsLoading,
    onAddAdditionalMortgage,
  ]);

  return (
    <Box className={className}>
      <ContentHeadline
        controls={
          <Button
            data-testid={PropertyAndLoanTestIds.ADDITIONAL_FINANCING_ADD_BUTTON}
            disabled={isLoading}
            size="small"
            startIcon={<AddBoxIcon size={IconSize[16]} />}
            variant="outlined"
            onClick={onAddAdditionalMortgage}
          >
            Add Additional Mortgage
          </Button>
        }
        title="Additional Financing"
      />

      <Box sx={boxStyle}>
        {additionalMortgagesKeys?.length ? (
          _.orderBy(
            additionalMortgagesKeys,
            [
              key => {
                const newAddedIndex = newAddedAdditionalMortgages.indexOf(key);

                if (newAddedIndex !== -1) {
                  return newAddedIndex;
                }

                return PERCENTAGE + Number(key.split('-').findLast(Boolean));
              },
            ],
            ['asc'],
          ).map(fieldKey => {
            return (
              <AdditionalMortgageForm
                applicationData={applicationData}
                changedFields={changedFields}
                className={className}
                disabledPendingChangesHighlight={disabledPendingChangesHighlight}
                entitySideData={entitySideData}
                fieldKey={fieldKey}
                form={form}
                handleChange={handleChange}
                isLoading={isLoading}
                isSaving={isSaving}
                key={fieldKey}
                pendingChanges={pendingChanges}
                setEntityConfig={setEntityConfig}
                setEntitySideData={setEntitySideData}
                setForm={setForm}
                setInForm={setInForm}
                setValidationErrors={setValidationErrors}
                staticValidateField={staticValidateField}
                validateField={validateField}
                validationErrors={validationErrors}
                onDeleteAdditionalMortgage={onDeleteAdditionalMortgage}
                onRestoreAdditionalMortgage={onRestoreAdditionalMortgage}
              />
            );
          })
        ) : (
          <NoAvailableMessageContent message={isLoading ? 'Loading' : 'No results'} />
        )}
      </Box>
    </Box>
  );
};

AdditionalFinancing.propTypes = pagesPropTypes;

export default memo(AdditionalFinancing);
